/**
 * Federal Capture Loop Frontend Service
 * 
 * API client for game theory-driven federal prime capture
 */

import api from './api';

// ==========================================
// TYPES
// ==========================================

export interface Prime {
  id: string;
  name: string;
  cage_code?: string;
  duns_number?: string;
  uei_number?: string;
  website?: string;
  size: 'small' | 'mid' | 'large';
  naics_codes: string[];
  agencies: string[];
  contract_vehicles: string[];
  uses_subcontractors: boolean;
  has_partner_program: boolean;
  partner_program_url?: string;
  annual_revenue?: { total: number; federal_pct: number };
  employee_count?: number;
  headquarters?: { city: string; state: string; region: string };
  notes?: string;
  is_active: boolean;
  pas_score?: number;
  passes_all_gates?: boolean;
}

export interface PrimeContact {
  id: string;
  prime_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  title?: string;
  role_type: 'bd' | 'capture' | 'procurement' | 'technical' | 'executive' | 'other';
  seniority: 'entry' | 'mid' | 'senior' | 'director' | 'vp' | 'c-level';
  is_decision_maker: boolean;
  relationship_status: 'cold' | 'warm' | 'engaged' | 'partner';
}

export interface PrimeScore {
  id: string;
  prime_id: string;
  version: number;
  pas_score: number;
  win_likelihood: number;
  deal_fit: number;
  relationship_accessibility: number;
  delivery_feasibility: number;
  onboarding_friction: number;
  bid_cost: number;
  compliance_risk: number;
  learning_velocity: number;
  passes_all_gates: boolean;
  score_breakdown: any;
}

export interface OutreachSequence {
  id: string;
  name: string;
  description?: string;
  channel: 'email' | 'call' | 'linkedin' | 'multi';
  target_type: 'prime_bd' | 'prime_capture' | 'prime_procurement' | 'general';
  steps: Array<{ day: number; type: string; template: string; subject?: string }>;
  total_steps: number;
  cadence_days: number;
  is_active: boolean;
  performance_stats: { sent: number; opened: number; replied: number; meetings: number };
}

export interface Meeting {
  id: string;
  prime_id: string;
  contact_id?: string;
  assigned_rep: string;
  title: string;
  meeting_type: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  notes?: string;
  action_items: any[];
  next_step: string;
}

export interface TeamingOpportunity {
  id: string;
  prime_id: string;
  opportunity_name: string;
  status: string;
  nda_signed_at?: string;
  vendor_approved_at?: string;
  onboarding_pct: number;
  scope_alignment_pct: number;
  estimated_value?: number;
}

export interface BidOpportunity {
  id: string;
  prime_id: string;
  teaming_id?: string;
  title: string;
  bid_type: string;
  agency?: string;
  our_role: string;
  stage: string;
  due_date?: string;
  estimated_value?: number;
  our_scope_value?: number;
  win_probability: number;
}

export interface FederalCaptureDashboard {
  primes: {
    total: number;
    usingSubs: number;
    withPrograms: number;
    topPrimes: Prime[];
  };
  contacts: {
    total: number;
    warm: number;
  };
  outreach: {
    last30Days: number;
    meetingsBooked: number;
  };
  teaming: {
    total: number;
    active: number;
  };
  bids: {
    total: number;
    inPipeline: number;
    pipelineValue: number;
  };
  artifacts: {
    total: number;
    shareable: number;
    withReferences: number;
  };
}

export interface OutreachPerformance {
  period_days: number;
  email: {
    sent: number;
    opened: number;
    replied: number;
    open_rate: string;
    reply_rate: string;
  };
  calls: {
    attempted: number;
    connected: number;
    connect_rate: string;
  };
  meetings: {
    scheduled: number;
    booking_rate: string;
  };
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
  performance: { sent: number; opened: number; replied: number };
}

export interface CallScript {
  id: string;
  name: string;
  opening: string;
  bridge: string;
  value_prop: string;
  qualifying_questions: string[];
  close: string;
  objection_handling?: Record<string, string>;
}

// ==========================================
// API FUNCTIONS
// ==========================================

const BASE = '/stratagem/federal';

// Dashboard
export const getDashboard = async (): Promise<FederalCaptureDashboard> => {
  const response = await api.get(`${BASE}/dashboard`);
  return response.data.data;
};

// Primes
export const ingestPrime = async (data: Partial<Prime>): Promise<Prime> => {
  const response = await api.post(`${BASE}/primes`, data);
  return response.data.data;
};

export const getPrimes = async (filters?: {
  size?: string;
  usesSubcontractors?: boolean;
  hasPartnerProgram?: boolean;
  minPas?: number;
  limit?: number;
}): Promise<Prime[]> => {
  const response = await api.get(`${BASE}/primes`, { params: filters });
  return response.data.data;
};

export const getTopPrimes = async (limit = 20): Promise<Prime[]> => {
  const response = await api.get(`${BASE}/primes/top`, { params: { limit } });
  return response.data.data;
};

export const getPrime = async (id: string): Promise<Prime> => {
  const response = await api.get(`${BASE}/primes/${id}`);
  return response.data.data;
};

export const updatePrime = async (id: string, data: Partial<Prime>): Promise<Prime> => {
  const response = await api.patch(`${BASE}/primes/${id}`, data);
  return response.data.data;
};

// PAS Scoring
export const calculatePAS = async (primeId: string, overrides?: Partial<PrimeScore>): Promise<PrimeScore> => {
  const response = await api.post(`${BASE}/primes/${primeId}/score`, overrides);
  return response.data.data;
};

// Contacts
export const addContact = async (primeId: string, data: Partial<PrimeContact>): Promise<PrimeContact> => {
  const response = await api.post(`${BASE}/primes/${primeId}/contacts`, data);
  return response.data.data;
};

export const getContacts = async (primeId: string): Promise<PrimeContact[]> => {
  const response = await api.get(`${BASE}/primes/${primeId}/contacts`);
  return response.data.data;
};

export const updateContactRelationship = async (
  contactId: string,
  status: PrimeContact['relationship_status'],
  notes?: string
): Promise<void> => {
  await api.patch(`${BASE}/contacts/${contactId}/relationship`, { status, notes });
};

// Outreach Sequences
export const createSequence = async (data: Partial<OutreachSequence>): Promise<OutreachSequence> => {
  const response = await api.post(`${BASE}/sequences`, data);
  return response.data.data;
};

export const getSequences = async (activeOnly = true): Promise<OutreachSequence[]> => {
  const response = await api.get(`${BASE}/sequences`, { params: { activeOnly } });
  return response.data.data;
};

export const launchSequence = async (
  sequenceId: string,
  primeId: string,
  contactId: string | null,
  assignedRep: string
): Promise<any> => {
  const response = await api.post(`${BASE}/sequences/${sequenceId}/launch`, {
    primeId,
    contactId,
    assignedRep,
  });
  return response.data.data;
};

// Outreach Performance
export const getOutreachPerformance = async (repId?: string, days = 30): Promise<OutreachPerformance> => {
  const response = await api.get(`${BASE}/outreach/performance`, { params: { repId, days } });
  return response.data.data;
};

// Meetings
export const scheduleMeeting = async (data: Partial<Meeting>): Promise<Meeting> => {
  const response = await api.post(`${BASE}/meetings`, data);
  return response.data.data;
};

export const getMeetings = async (filters?: { primeId?: string; rep?: string; status?: string }): Promise<Meeting[]> => {
  const response = await api.get(`${BASE}/meetings`, { params: filters });
  return response.data.data;
};

export const completeMeeting = async (
  meetingId: string,
  notes: string,
  actionItems: any[],
  nextStep: string
): Promise<Meeting> => {
  const response = await api.patch(`${BASE}/meetings/${meetingId}/complete`, {
    notes,
    actionItems,
    nextStep,
  });
  return response.data.data;
};

// Teaming Opportunities
export const createTeamingOpportunity = async (data: Partial<TeamingOpportunity>): Promise<TeamingOpportunity> => {
  const response = await api.post(`${BASE}/teaming`, data);
  return response.data.data;
};

export const getTeamingOpportunities = async (filters?: { primeId?: string; status?: string }): Promise<TeamingOpportunity[]> => {
  const response = await api.get(`${BASE}/teaming`, { params: filters });
  return response.data.data;
};

export const updateTeamingStatus = async (id: string, status: string, additionalData?: any): Promise<TeamingOpportunity> => {
  const response = await api.patch(`${BASE}/teaming/${id}/status`, { status, ...additionalData });
  return response.data.data;
};

// Bid Opportunities
export const createBidOpportunity = async (data: Partial<BidOpportunity>): Promise<BidOpportunity> => {
  const response = await api.post(`${BASE}/bids`, data);
  return response.data.data;
};

export const getBidOpportunities = async (filters?: { primeId?: string; stage?: string; dueWithinDays?: number }): Promise<BidOpportunity[]> => {
  const response = await api.get(`${BASE}/bids`, { params: filters });
  return response.data.data;
};

export const updateBidStage = async (id: string, stage: string, winProbability?: number): Promise<BidOpportunity> => {
  const response = await api.patch(`${BASE}/bids/${id}/stage`, { stage, winProbability });
  return response.data.data;
};

// Templates
export const getColdEmailTemplates = async (): Promise<EmailTemplate[]> => {
  const response = await api.get(`${BASE}/templates/cold-email`);
  return response.data.data;
};

export const getColdCallScripts = async (): Promise<CallScript[]> => {
  const response = await api.get(`${BASE}/templates/cold-call`);
  return response.data.data;
};

// Velocity
export const checkVelocity = async (
  resourceType: 'outreach_email' | 'outreach_call' | 'prime_touch',
  scope = 'system',
  period = 'daily'
): Promise<{ allowed: boolean; current: number; limit: number; resetAt: Date }> => {
  const response = await api.get(`${BASE}/velocity/${resourceType}`, { params: { scope, period } });
  return response.data.data;
};

// Export all as default
const federalCaptureService = {
  getDashboard,
  ingestPrime,
  getPrimes,
  getTopPrimes,
  getPrime,
  updatePrime,
  calculatePAS,
  addContact,
  getContacts,
  updateContactRelationship,
  createSequence,
  getSequences,
  launchSequence,
  getOutreachPerformance,
  scheduleMeeting,
  getMeetings,
  completeMeeting,
  createTeamingOpportunity,
  getTeamingOpportunities,
  updateTeamingStatus,
  createBidOpportunity,
  getBidOpportunities,
  updateBidStage,
  getColdEmailTemplates,
  getColdCallScripts,
  checkVelocity,
};

export default federalCaptureService;
